import {v4 as uuid} from 'uuid';

import type {Nullish} from 'shared/web';

const MATRIX_PUT_KEY_PREFIX = 'variantMatrix:';
const CELL_PATCH_KEY_PREFIX = 'variant:';

/** The kinds of item a grouped create makes. */
type CreatedKind = 'trim' | 'colour' | 'upholstery' | 'package';

/** The target keys of batch entries: two entries with one key are refused in one group. */
export const pricedItemGroupKeys = {
  /** A change of a stored trim: its update, archive or equipment (one per batch, `duplicateTarget`). */
  trim: (trimId: string) => `trim:${trimId}`,
  /** An archive of a stored colour, or a replace of its trims (`colourTrims.replace`). */
  colour: (colourId: string) => `colour:${colourId}`,
  /** An archive of a stored upholstery. */
  upholstery: (upholsteryId: string) => `upholstery:${upholsteryId}`,
  /** An update or archive of a stored package. */
  package: (packageId: string) => `package:${packageId}`,
  /**
   * A library feature write (`feature.update`) of a package entry: an extra key, as the BE refuses
   * two writes of one feature in one batch (`duplicateTarget`).
   */
  feature: (featureId: string) => `feature:${featureId}`,
  /** An enable switch of a Variant (`variantMatrix.patchCell`). */
  variant: (variantId: string) => `${CELL_PATCH_KEY_PREFIX}${variantId}`,
  /** The new matrix cells of a model year; they merge into one `variantMatrix.put`. */
  matrixPut: (modelYearId: string) => `${MATRIX_PUT_KEY_PREFIX}${modelYearId}`,
  /**
   * The key and the client id of a grouped create: each create is its own target. The create sends
   * the id as `id`, so later operations of the batch can name the item (F1 addendum 4.4). It is a
   * UUID, because the BE stores it as the id of the created item (`idTaken` when it is in use).
   */
  newCreate: (kind: CreatedKind) => {
    const createdId = uuid();
    return {key: `${kind}:new:${createdId}`, createdId};
  },
  /**
   * One value of `usedValues` (`valueTaken`), or none for an empty value, as the BE refuses two
   * operations of one batch that use one name or code (`duplicateValue`). A name is trimmed and keeps
   * its case; a code (also the order code of a Variant) is trimmed and upper-cased, as
   * `CatalogueCodeUniqueness` compares it.
   */
  usedValue: (
    itemType: 'colour' | 'upholstery' | 'package' | 'variant',
    kind: 'name' | 'code',
    raw: string | Nullish
  ) => {
    const trimmed = (raw ?? '').trim();
    const value = kind === 'code' ? trimmed.toUpperCase() : trimmed;
    return value === '' ? [] : [`${itemType}:${kind}:${value}`];
  },
  isMatrixPut: (key: string) => key.startsWith(MATRIX_PUT_KEY_PREFIX),
  isCellPatch: (key: string) => key.startsWith(CELL_PATCH_KEY_PREFIX),
};
